<template>
  <app-content class="report-view">
    <paged-report-panel
      v-model="pagedFilter"
      :total-rows="totalRecords"
      :loading="loading"
      :show-search-term="false"
      :search-term-property="'genericSearch'"
      @input="onRefreshSearch"
    >
      <s-table
        class="report-view__table mt-2"
        :busy="loading"
        :fields="fields"
        :items="history"
        :empty-text="$t('general.empty-list')"
      >
        <template #cell(dateTime)="{ item }">
          {{ item.dateTime | dateTimeFilter({ format: formats.DATETIME_SHORT_WITH_SECONDS }) }}
        </template>
        <template #cell(operationType)="{ item }">
          {{ item.operationType | emptyValueFallbackFilter }}
        </template>
        <template #cell(username)="{ item }">
          {{ item.username | emptyValueFallbackFilter }}
        </template>
        <template #cell(complement)="{ item }">
          {{ item.complement | emptyValueFallbackFilter }}
        </template>
        <template #cell(functionality)="{ item }">
          {{ item.functionality | emptyValueFallbackFilter }}
        </template>
        <template #cell(companyName)="{ item }">
          {{ item.companyName | emptyValueFallbackFilter }}
        </template>
        <template #cell(regionalName)="{ item }">
          {{ item.regionalName | emptyValueFallbackFilter }}
        </template>
        <template #cell(installationName)="{ item }">
          {{ item.installationName | emptyValueFallbackFilter }}
        </template>
        <template #cell(assetName)="{ item }">
          {{ item.assetName | emptyValueFallbackFilter }}
        </template>
        <template #cell(moduleName)="{ item }">
          {{ item.moduleName | emptyValueFallbackFilter }}
        </template>
        <template #cell(action)="{ item }">
          <b-button
            v-if="$hasUserPermission('PER021')"
            class="no-internal-print"
            variant="outline-primary"
            size="sm"
            :disabled="isDownloading(item.id)"
            @click="download(item.id)"
          >
            <s-loader
              v-if="isDownloading(item.id)"
              class="report-view__download-loader"
            />
            <i
              v-else
              class="fas fa-download"
              aria-hidden="true"
            />
          </b-button>
        </template>
      </s-table>
    </paged-report-panel>
  </app-content>
</template>

<script>
import emptyValueFallbackFilter from '@/filters/emptyValueFallbackFilter';
import PagedReportPanel from '@/domain/components/generalPanel/reports/PagedReportPanel';
import loadingMixin from '@/services/loadingMixin';
import { formats } from '@/constants/localizer';
import OperationHistoryService from '@/domain/services/generalPanel/reports/operationHistory';
import SessionManager from '@/services/sessionStorageManager';
import fields from '@/domain/data/generalPanel/reports/operationsHistory/reportFields';

export default {
  components: {
    PagedReportPanel,
  },
  mixins: [
    loadingMixin,
  ],
  data: () => ({
    formats,
    pagedFilter: {
      pageSize: 15,
      page: 1,
      filter: {},
    },
    totalRecords: 0,
    history: [],
    loadingDownloadIds: [],
  }),
  computed: {
    fields() {
      return fields(this);
    },
    isHistoryEmpty() {
      return (this.history || []).length === 0;
    },
  },
  filters: {
    emptyValueFallbackFilter,
  },
  created() {
    this.getFilterFromSessionStorage();
    this.getHistory();
  },
  methods: {
    isDownloading(id) {
      return this.loadingDownloadIds.includes(id);
    },
    removeLoadingDownloadId(id) {
      this.loadingDownloadIds = this.loadingDownloadIds.filter(loadingId => loadingId !== id);
    },
    addLoadingDownloadId(id) {
      this.loadingDownloadIds.push(id);
    },
    download(id) {
      this.addLoadingDownloadId(id);
      OperationHistoryService.downloadTemplate(id)
        .finally(() => {
        this.removeLoadingDownloadId(id);
      });
    },
    getHistory() {
      this.startLoading();
      return OperationHistoryService.getOperationHistory(this.pagedFilter)
        .then((response) => {
          if (response.partial) {
            this.$pushAlert({ code: 'ME096' });
            return;
          }
          if (response.data.length === 0) {
            this.$pushAlert({ code: 'MA009' });
            return;
          }
          this.totalRecords = response.total;
          this.history = response.data;
          if (this.pagedFilter.page === 1) {
            this.$pushAlert({ code: 'MS011' });
          }
        })
        .finally(() => this.endLoading());
    },
    onRefreshSearch(input) {
      this.pagedFilter = input;
      this.getHistory();
    },
    tryJsonParse(item) {
      try {
        return JSON.parse(item);
      } catch (e) {
        this.$pushAlert({
          message: this.$t('reports.filter-error'),
          type: 'warning',
        });
        return {};
      }
    },
    getFilterFromSessionStorage() {
      const { filter } = this.$route.query;
      const storageItem = SessionManager.getItem(filter);
      this.pagedFilter = { ...this.pagedFilter.filter, ...this.tryJsonParse(storageItem) };
      this.pagedFilter.page = 1;
    },
  },
};
</script>
<style lang="sass" scoped>
@import src/styles/defaults/mobile-first.sass
.report-view
  &__download-loader
    height: 20px

  &__download-btn:hover
    background-color: transparent

  &__table
    height: calc(100vh - 235px)

    ::v-deep .table-cell
      &__complement
        max-width: 32rem
        word-break: break-word
</style>
